/**
 * 处理Markdown中的列表元素的完整性
 */
import { LogDebugFunction } from '../utils/markdownTypes';

/**
 * 处理普通无序列表
 */
export const processUnorderedList = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 无序列表处理 - 确保 - 及其空格和至少一个内容字符一起显示
  if (/- *$/.test(analyzedText)) {
    const listMatch = remainingText.match(/^(-\s+)([^ \n])?/);
    if (listMatch) {
      const dashWithSpace = listMatch[1]?.length || 0;
      const minRequired = dashWithSpace + 1; // +1 表示至少一个内容字符
      
      if (charsToShow < minRequired) {
        return logDebugInfo('无序列表', minRequired - charsToShow);
      }
    }
  }
  
  return 0;
};

/**
 * 处理普通有序列表
 */
export const processOrderedList = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 有序列表处理
  if (/\d+\. *$/.test(analyzedText)) {
    const numberedListMatch = remainingText.match(/^(\d+\.\s+)([^ \n])?/);
    if (numberedListMatch) {
      const numberWithDotSpace = numberedListMatch[1]?.length || 0;
      const minRequired = numberWithDotSpace + 1; // +1 表示至少一个内容字符
      
      if (charsToShow < minRequired) {
        return logDebugInfo('有序列表', minRequired - charsToShow);
      }
    }
  }
  
  return 0;
};

/**
 * 处理缩进的无序列表(非行首情况)
 */
export const processIndentedUnorderedList = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 缩进的无序列表: "  - 文本" 或 "    - 文本" (非行首情况)
  if (/[ \t]+ *- *$/.test(analyzedText)) {
    const indentedListMatch = remainingText.match(/^([ \t]+ *- *\s*)([^ \n])?/);
    if (indentedListMatch) {
      const indentWithDashSpace = indentedListMatch[1]?.length || 0;
      const minRequired = indentWithDashSpace + 1; // +1 表示至少一个内容字符
      
      console.log('匹配到二级无序列表:', {
        匹配文本: indentedListMatch[0],
        前缀: indentedListMatch[1],
        内容: indentedListMatch[2] || '无',
        总长度: minRequired
      });
      
      if (charsToShow < minRequired) {
        return logDebugInfo('二级无序列表', minRequired - charsToShow);
      }
    }
  }
  
  return 0;
};

/**
 * 处理缩进的有序列表(非行首情况)
 */
export const processIndentedOrderedList = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 缩进的有序列表: "  1. 文本" 或 "    1. 文本" (非行首情况)
  if (/[ \t]+ *\d+\. *$/.test(analyzedText)) {
    const indentedNumberedListMatch = remainingText.match(/^([ \t]+ *\d+\.\s*)([^ \n])?/);
    if (indentedNumberedListMatch) {
      const indentWithNumberDotSpace = indentedNumberedListMatch[1]?.length || 0;
      const minRequired = indentWithNumberDotSpace + 1; // +1 表示至少一个内容字符
      
      console.log('匹配到二级有序列表:', {
        匹配文本: indentedNumberedListMatch[0],
        前缀: indentedNumberedListMatch[1],
        内容: indentedNumberedListMatch[2] || '无',
        总长度: minRequired
      });
      
      if (charsToShow < minRequired) {
        return logDebugInfo('二级有序列表', minRequired - charsToShow);
      }
    }
  }
  
  return 0;
};

/**
 * 处理行首的列表项（特殊情况）
 */
export const processListAtLineStart = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 只在文本开始或换行后检查
  if (startIdx === 0 || text.charAt(startIdx - 1) === '\n') {
    // 匹配缩进的无序列表开头与粗体/斜体组合
    const indentedListStyleStartMatch = remainingText.match(/^([ \t]+)(-[ \t]*)(\*\*|\*)?/);
    if (indentedListStyleStartMatch) {
      const styleMarker = indentedListStyleStartMatch[3] || '';
      const fullIndentDashStyle = indentedListStyleStartMatch[1].length + 
                                  indentedListStyleStartMatch[2].length + 
                                  styleMarker.length;
      
      console.log('匹配到二级列表开头与格式化组合:', {
        匹配文本: indentedListStyleStartMatch[0],
        缩进: indentedListStyleStartMatch[1],
        破折号: indentedListStyleStartMatch[2],
        格式标记: styleMarker,
        总长度: fullIndentDashStyle
      });
      
      // 为了确保显示完整，加上一个内容字符的长度
      const minRequired = fullIndentDashStyle + 1; // +1 表示至少一个内容字符
      if (charsToShow < minRequired) {
        return logDebugInfo('二级列表格式化-开头', minRequired - charsToShow);
      }
    }
    
    // 匹配缩进的无序列表开头
    const indentedUnorderedListStartMatch = remainingText.match(/^([ \t]+)(-[ \t]*)/);
    if (indentedUnorderedListStartMatch) {
      const fullIndentDash = indentedUnorderedListStartMatch[0].length;
      console.log('匹配到二级无序列表开头:', {
        匹配文本: indentedUnorderedListStartMatch[0],
        缩进: indentedUnorderedListStartMatch[1],
        破折号: indentedUnorderedListStartMatch[2],
        总长度: fullIndentDash
      });
      
      // 为了确保显示完整，加上一个内容字符的长度
      const minRequired = fullIndentDash + 1; // +1 表示至少一个内容字符
      if (charsToShow < minRequired) {
        return logDebugInfo('二级无序列表开头', minRequired - charsToShow);
      }
    }
    
    // 匹配缩进的有序列表开头与粗体/斜体组合
    const indentedOrderedListStyleStartMatch = remainingText.match(/^([ \t]+)(\d+\.[ \t]*)(\*\*|\*)?/);
    if (indentedOrderedListStyleStartMatch) {
      const styleMarker = indentedOrderedListStyleStartMatch[3] || '';
      const fullIndentNumberStyle = indentedOrderedListStyleStartMatch[1].length + 
                                   indentedOrderedListStyleStartMatch[2].length + 
                                   styleMarker.length;
      
      console.log('匹配到二级有序列表开头与格式化组合:', {
        匹配文本: indentedOrderedListStyleStartMatch[0],
        缩进: indentedOrderedListStyleStartMatch[1],
        序号: indentedOrderedListStyleStartMatch[2],
        格式标记: styleMarker,
        总长度: fullIndentNumberStyle
      });
      
      // 为了确保显示完整，加上一个内容字符的长度
      const minRequired = fullIndentNumberStyle + 1; // +1 表示至少一个内容字符
      if (charsToShow < minRequired) {
        return logDebugInfo('二级有序列表格式化-开头', minRequired - charsToShow);
      }
    }
    
    // 匹配缩进的有序列表开头
    const indentedOrderedListStartMatch = remainingText.match(/^([ \t]+)(\d+\.[ \t]*)/);
    if (indentedOrderedListStartMatch) {
      const fullIndentNumber = indentedOrderedListStartMatch[0].length;
      console.log('匹配到二级有序列表开头:', {
        匹配文本: indentedOrderedListStartMatch[0],
        缩进: indentedOrderedListStartMatch[1],
        序号: indentedOrderedListStartMatch[2],
        总长度: fullIndentNumber
      });
      
      // 为了确保显示完整，加上一个内容字符的长度
      const minRequired = fullIndentNumber + 1; // +1 表示至少一个内容字符
      if (charsToShow < minRequired) {
        return logDebugInfo('二级有序列表开头', minRequired - charsToShow);
      }
    }
  }
  
  return 0;
};

/**
 * 处理行首的列表项与粗体组合（特殊优先处理）
 */
export const processIndentedListBoldAtLineStart = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 只在文本开始或换行后检查
  if (startIdx === 0 || text.charAt(startIdx - 1) === '\n') {
    // 匹配缩进的无序列表开头与粗体组合
    const indentedListBoldStartMatch = remainingText.match(/^([ \t]+)(-[ \t]*\*\*)([^*]*)(\*\*)([：:])?/);
    if (indentedListBoldStartMatch) {
      const indentPart = indentedListBoldStartMatch[1];
      const listPart = indentedListBoldStartMatch[2];
      const contentPart = indentedListBoldStartMatch[3];
      const closingBold = indentedListBoldStartMatch[4];
      const colon = indentedListBoldStartMatch[5] || '';
      
      const fullMatch = indentedListBoldStartMatch[0].length;
      
      console.log('匹配到行首二级列表+粗体组合:', {
        匹配文本: indentedListBoldStartMatch[0],
        缩进: indentPart,
        列表标记: listPart,
        内容: contentPart,
        闭合粗体: closingBold,
        冒号: colon,
        总长度: fullMatch
      });
      
      if (charsToShow < fullMatch) {
        return logDebugInfo('行首二级列表粗体-完整', fullMatch - charsToShow);
      }
    }
    
    // 匹配缩进的有序列表开头与粗体组合
    const indentedOrderedListBoldStartMatch = remainingText.match(/^([ \t]+)(\d+\.[ \t]*\*\*)([^*]*)(\*\*)([：:])?/);
    if (indentedOrderedListBoldStartMatch) {
      const indentPart = indentedOrderedListBoldStartMatch[1];
      const listPart = indentedOrderedListBoldStartMatch[2];
      const contentPart = indentedOrderedListBoldStartMatch[3];
      const closingBold = indentedOrderedListBoldStartMatch[4];
      const colon = indentedOrderedListBoldStartMatch[5] || '';
      
      const fullMatch = indentedOrderedListBoldStartMatch[0].length;
      
      console.log('匹配到行首二级有序列表+粗体组合:', {
        匹配文本: indentedOrderedListBoldStartMatch[0],
        缩进: indentPart,
        列表标记: listPart,
        内容: contentPart,
        闭合粗体: closingBold,
        冒号: colon,
        总长度: fullMatch
      });
      
      if (charsToShow < fullMatch) {
        return logDebugInfo('行首二级有序列表粗体-完整', fullMatch - charsToShow);
      }
    }
  }
  
  return 0;
}; 